import React, { PureComponent } from 'react';
import { type WithStyles, createStyles } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { colors } from '@skyslope/mache';
import { areGroupRulesValid } from '../common/utilities';
import type { IBlock, IGroup } from '../store/types';
import Checkbox from './Checkbox';
import Info from './common/Info';
import Tooltip from './Tooltip';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const styles = () =>
  createStyles({
    rnd: {
      zIndex: 3,
    },
    border: {
      margin: -4,
      padding: 4,
      backgroundColor: 'rgba(183, 220, 255, 0.15)',
      outline: `1px dashed ${colors.blue[100]}`,
      position: 'absolute',
      display: 'flex',
      justifyContent: 'center',
    },
    groupPlus: {
      height: 24,
      width: 24,
      backgroundColor: 'blue',
      cursor: 'pointer',
      position: 'absolute',
      top: 'calc(100% + 8px)',
      left: 'calc(50% - 12px)',
      color: '#ffffff',
      fontSize: '22px',
      textAlign: 'center',
      borderRadius: 4,
    },
  });

interface IProps extends WithStyles<typeof styles> {
  blocks: IBlock[];
  envelopeStatus: string;
  zoom: number;
  clickSignature: (blockId: string, tabIndex?: number) => void;
  updateBlock: (blockId: string, updates: { value?: boolean; status?: string; blockType?: string }) => void;
  group: IGroup;
  tabIndex: number;
  signingExperienceBlocks: IBlock[];
  signingExperienceComplete: boolean;
  currentSelectedBlockIndex: number | null;
  tabToBlock: (nextIndex: number) => void;
  pageDimensions: { width?: number; height?: number };
  scrollToBlock: (blockElement: HTMLDivElement) => void;
  setBlocksInGroupToComplete: (blocks: IBlock[]) => void;
}

interface IState {
  x: number;
  y: number;
  width: number;
  height: number;
  groupSelected: boolean;
  dismissedTooltip: boolean;
}

export class GroupBox extends PureComponent<IProps, IState> {
  groupRef: React.RefObject<HTMLDivElement>;
  componentDidUpdate() {
    const { tabIndex, currentSelectedBlockIndex, signingExperienceBlocks, blocks, group, pageDimensions } = this.props;
    const coords = GroupBox.getGroupBoxCords(blocks);
    let currentBlock = null;
    if (currentSelectedBlockIndex !== null) {
      currentBlock = blocks.find(
        (block) => block.blockId === signingExperienceBlocks[currentSelectedBlockIndex!].blockId
      );
    }
    if (
      (currentSelectedBlockIndex !== null && currentSelectedBlockIndex === tabIndex) ||
      (currentBlock && currentBlock!.groupId === group.id)
    ) {
      this.props.scrollToBlock(this.groupRef.current!);
    } else {
      this.groupRef!.current!.blur();
    }
    if (this.groupContainsTabIndex()) {
      this.setState({
        groupSelected: true,
      });
    } else {
      this.setState({
        groupSelected: false,
        dismissedTooltip: false,
      });
    }
    if (pageDimensions.height && pageDimensions.width) {
      this.setState({
        width: coords.width,
        height: coords.height,
      });
    }
  }

  constructor(props: IProps) {
    super(props);
    const coords = GroupBox.getGroupBoxCords(props.blocks);
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    this.state = {
      x: coords.x,
      y: coords.y,
      width: coords.width,
      height: coords.height,
      groupSelected: false,
    };
    this.groupRef = React.createRef<HTMLDivElement>();
  }

  static getGroupBoxCords = (blocks: IBlock[]) => {
    let topLeftX = Infinity;
    let topLeftY = Infinity;
    let bottomRightX = 0;
    let bottomRightY = 0;

    blocks.forEach((block) => {
      if (block.x! < topLeftX) {
        topLeftX = block.x!;
      }
      if (block.y! < topLeftY) {
        topLeftY = block.y!;
      }
      if (block.x! + block.width! > bottomRightX) {
        bottomRightX = block.x! + block.width!;
      }
      if (block.y! + block.height! > bottomRightY) {
        bottomRightY = block.y! + block.height!;
      }
    });
    return {
      x: topLeftX,
      y: topLeftY,
      width: bottomRightX - topLeftX,
      height: bottomRightY - topLeftY,
    };
  };

  getRemainingCheckBoxes = () => {
    const uncheckedBoxes = this.props.blocks.reduce((total, block) => {
      if (block.value) {
        return total + 1;
      }
      return total + 0;
    }, 0);
    if (uncheckedBoxes > this.props.group.validation.value) {
      return 0;
    }
    return Math.abs(uncheckedBoxes - this.props.group.validation.value);
  };

  handleUpdate = (newValue: boolean, blockId?: string) => {
    let currentBlock: IBlock | undefined;
    if (this.props.currentSelectedBlockIndex !== null) {
      currentBlock = this.props.blocks.find(
        (block) => block.blockId === this.props.signingExperienceBlocks[this.props.currentSelectedBlockIndex!].blockId
      );
    }
    if (!currentBlock) {
      this.props.tabToBlock(this.props.tabIndex);
    }

    if (blockId) {
      this.props.updateBlock(blockId, {
        value: newValue,
        status: newValue === true ? 'Complete' : 'Pending',
      });
    }

    if (currentBlock && areGroupRulesValid({ ...currentBlock, value: newValue }, this.props.group, this.props.blocks)) {
      this.props.setBlocksInGroupToComplete(this.props.blocks);
    }
  };

  generateDataSpec = () => {
    const validationRule = this.props.group.validation.rule;
    if (validationRule === 'eq' || validationRule === 'gte') {
      return 'requiredTooltip';
    } else {
      return 'optionalTooltip';
    }
  };

  groupContainsTabIndex = () => {
    const { signingExperienceBlocks, currentSelectedBlockIndex, blocks } = this.props;
    for (let i = 0; i < blocks.length; i++) {
      const currentBlock = blocks[i];
      const currentIndex = signingExperienceBlocks.find(
        (currentSigningExperienceBlock) => currentSigningExperienceBlock.blockId === currentBlock.blockId
      );
      if (signingExperienceBlocks.indexOf(currentIndex!) === currentSelectedBlockIndex) {
        return true;
      }
    }
    return false;
  };

  createTooltipString = () => {
    const validationRule = this.props.group.validation.rule;
    const remainingBoxes = this.getRemainingCheckBoxes();
    const validationStrings = {
      gte: `Select at least ${remainingBoxes} box${remainingBoxes > 1 ? 'es' : ''}`,
      lte: `Select at most ${remainingBoxes} box${remainingBoxes > 1 ? 'es' : ''}`,
      eq: `Select ${remainingBoxes} box${remainingBoxes > 1 ? 'es' : ''}`,
    };
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const string = remainingBoxes ? validationStrings[validationRule] : '';
    const tooltipString =
      validationRule === 'gte' || validationRule === 'eq' ? (
        <>
          <span style={{ color: '#D20004', marginRight: '2px' }}>*</span>Required &mdash; {string}
        </>
      ) : (
        <>Optional - {string}</>
      );
    return tooltipString;
  };

  render() {
    if (this.props.envelopeStatus === 'Completed') return null;

    const blockCounts = {
      Signature: 0,
      Initials: 0,
      Text: 0,
      Checkbox: 0,
      DateSigned: 0,
    };
    const tooltipString = this.createTooltipString();
    const remainingBoxes = this.getRemainingCheckBoxes();
    let disabled = false;
    const validationRule = this.props.group.validation.rule;
    if ((validationRule === 'lte' && remainingBoxes === 0) || (validationRule === 'eq' && remainingBoxes === 0)) {
      disabled = true;
    }
    return (
      <>
        <div
          className={this.props.classes.border}
          style={{
            width: `${(this.state.width / this.props.pageDimensions.width!) * 100}%`,
            height: `${(this.state.height / this.props.pageDimensions.height!) * 100}%`,
            top: `${(this.state.y / this.props.pageDimensions.height!) * 100}%`,
            left: `${(this.state.x / this.props.pageDimensions.width!) * 100}%`,
            justifyContent: this.state.width > this.state.height ? 'flex-start' : 'end',
          }}
        >
          <Tooltip
            visible={!!remainingBoxes && this.state.groupSelected && !this.state.dismissedTooltip}
            zoom={this.props.zoom}
            data-spec={this.generateDataSpec()}
            centered={this.state.height > this.state.width}
            closeTooltip={() => this.setState({ dismissedTooltip: true })}
            isGroup={true}
          >
            <>{tooltipString}</>
          </Tooltip>
          <div style={{ position: 'absolute', width: 'calc(100% - 8px)', height: 'calc(100% - 8px)' }}>
            {this.props.blocks.map((block, index) => {
              const b = {
                ...block,
                x: block.x - this.state.x,
                y: block.y - this.state.y,
              };
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${(b.x! / this.state.width) * 100}%`,
                    top: `${(b.y! / this.state.height) * 100}%`,
                    height: `${(block.height! / this.state.height) * 100}%`,
                    width: `${(block.width! / this.state.width) * 100}%`,
                  }}
                  key={index}
                  ref={this.groupRef}
                >
                  <Checkbox
                    // @ts-expect-error -- This code was written prior to enforcing lint rules
                    index={(blockCounts[block.blockType] += 1)}
                    checked={block.value!}
                    onClick={this.handleUpdate}
                    block={b}
                    disabled={disabled}
                    required={remainingBoxes !== 0 && this.props.signingExperienceComplete}
                    focused={this.state.groupSelected && !block.value! && remainingBoxes !== 0}
                    zoom={this.props.zoom}
                  />
                </div>
              );
            })}
          </div>
          <Info
            visible={!!remainingBoxes && this.state.groupSelected}
            toggleTooltip={() => this.setState({ dismissedTooltip: false })}
            zoom={this.props.zoom}
          />
        </div>
      </>
    );
  }
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withStyles(styles)(GroupBox);
